/*
 * Announcement surfaces on the host page: a full-bleed top banner and a small
 * chip above the launcher. Both render admin-authored titles as plain text;
 * the rich body lives inside the widget iframe.
 *
 * Same closed-shadow-root isolation as fab.css: selectors stay inside the
 * shadow tree and :host pins inherited text properties against host-page
 * styling.
 */

:host {
	--ink: #14141f;
	--ink-subtle: #6b6c80;
	--accent: hsl(260deg 100% 69%);
	--hairline: rgb(20 20 31 / 10%);

	/*
	 * Banner band, per category. The default is the product-update violet,
	 * which also carries an unrecognized kind from a newer widget build.
	 * `.banner[data-kind]` swaps the whole set – see "banner categories".
	 * The title runs across the whole band in white 14px, so every stop
	 * keeps at least 4.5:1 against white (WCAG AA for normal text).
	 */
	--band-from: #7b3fff;
	--band-mid: #8f45ff;
	--band-to: #9548f5;
	--band-ink: #7b3fff;
	--band-shadow: rgb(123 63 255 / 28%);

	z-index: 2147483646;
	font-family:
		system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
		sans-serif;
	font-size: 14px;
	line-height: 1.45;
	text-align: start;
	color-scheme: light;
}

:host([data-hidden]) {
	display: none;
}

/* Out of sight while the launcher is dragged, so it never floats apart from
   it. Visibility only – whether it shows at all is decided elsewhere. */
:host([data-launcher-moving]) {
	visibility: hidden;
}

/* --- shared parts ------------------------------------------------------- */

.title {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	font-weight: 600;
	white-space: nowrap;
}

.view {
	flex: none;
	padding: 6px 12px;
	font-size: 13px;
	font-weight: 600;
	color: #fff;
	cursor: pointer;
	background: var(--accent);
	border: 0;
	border-radius: 999px;
}

.dismiss {
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	padding: 0;
	color: var(--ink-subtle);
	cursor: pointer;
	background: transparent;
	border: 0;
	border-radius: 50%;
}

.dismiss:hover {
	color: var(--ink);
	background: rgb(20 20 31 / 6%);
}

/* --- top banner --------------------------------------------------------- */

/*
 * Full-bleed and flush with the top edge. The host page shifts down by the
 * bar's height (see `createPageOffset`), so the banner sits above the page
 * rather than floating over it – a docked band reads as part of the product,
 * a floating pill reads as browser chrome and gets skipped.
 */
:host([data-surface="banner"]) {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
}

.banner {
	position: relative;
	display: flex;
	gap: 12px;
	align-items: center;
	padding: 11px 12px 11px 16px;
	overflow: hidden;
	color: #fff;
	background: linear-gradient(
		135deg,
		var(--band-from),
		var(--band-mid) 60%,
		var(--band-to)
	);
	box-shadow: 0 6px 18px var(--band-shadow);
	animation: wl-slide-down 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

/*
 * A slow highlight crossing the band – the one ambient loop that keeps the
 * banner alive after the entrance without redrawing text.
 */
.sheen {
	position: absolute;
	inset: 0;
	pointer-events: none;
	background: linear-gradient(
		105deg,
		rgb(255 255 255 / 0%) 38%,
		rgb(255 255 255 / 22%) 50%,
		rgb(255 255 255 / 0%) 62%
	);
	background-repeat: no-repeat;
	background-size: 220% 100%;
	animation: wl-sheen 9s ease-in-out 1.4s infinite;
}

.badge {
	position: relative;
	display: inline-flex;
	flex: none;
	align-items: center;
	height: 20px;
	padding: 0 8px;
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.09em;

	/* A dark wash, not a white one – white text on a lightened band drops
	   under 4.5:1. */
	background: rgb(0 0 0 / 18%);
	border-radius: 6px;
}

/* An unrecognized kind has no label to show, so the chip collapses. */
.badge:empty {
	display: none;
}

.banner .title {
	position: relative;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: -0.005em;
}

.banner .view {
	position: relative;
	display: inline-flex;
	align-items: center;
	height: 28px;
	padding: 0 14px;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--band-ink);
	background: #fff;
	animation: wl-nudge 9s ease-in-out 1.4s infinite;
}

.banner .view:hover {
	background: rgb(255 255 255 / 88%);
}

/* "Got it" beside "View": the quiet twin, outlined on the band. */
.banner .got-it {
	position: relative;
	display: inline-flex;
	flex: none;
	align-items: center;
	height: 28px;
	padding: 0 14px;
	font: inherit;
	font-size: 12.5px;
	font-weight: 700;
	color: #fff;
	cursor: pointer;
	background: rgb(0 0 0 / 14%);
	border: 1px solid rgb(255 255 255 / 55%);
	border-radius: 999px;
}

.banner .got-it:hover {
	background: rgb(0 0 0 / 24%);
}

.banner .dismiss {
	position: relative;
	font-size: 17px;
	line-height: 1;
	color: rgb(255 255 255 / 72%);
}

.banner .dismiss:hover {
	color: #fff;
	background: rgb(255 255 255 / 18%);
}

/* --- banner categories -------------------------------------------------- */

/*
 * Changelog kinds normally stay quiet, but an admin can turn the banner on
 * for one. Each reuses the band of the color the rest of the product already
 * gives that kind – `fix` and `improvement` follow `--dot-warn` and
 * `--success`, `new` keeps the violet default, `resource` shares `--info`
 * with business update.
 */
.banner[data-kind="service_status"],
.banner[data-kind="fix"] {
	--band-from: #8a5000;
	--band-mid: #995c00;
	--band-to: #9c6a08;
	--band-ink: #7a5500;
	--band-shadow: rgb(232 154 0 / 28%);
}

.banner[data-kind="business_update"],
.banner[data-kind="resource"] {
	--band-from: #17639f;
	--band-mid: #1a6fbf;
	--band-to: #2a72d4;
	--band-ink: #1a6fbf;
	--band-shadow: rgb(61 157 255 / 28%);
}

.banner[data-kind="improvement"] {
	--band-from: #0b7150;
	--band-mid: #0c7d57;
	--band-to: #127f5d;
	--band-ink: #0b6b4a;
	--band-shadow: rgb(36 195 138 / 28%);
}

/* --- corner chip -------------------------------------------------------- */

:host([data-surface="chip"]) {
	position: fixed;
	right: 26px;
	bottom: 104px;
	width: min(340px, calc(100vw - 52px));
}

:host([data-surface="chip"][data-position="left"]) {
	right: auto;
	left: 26px;
}

/* The corner card: the home block's hero, floating over the host page. */
.chip {
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: stretch;
	padding: 12px 14px 14px;
	color: var(--ink);
	background: #fff;
	border: 1px solid var(--hairline);
	border-radius: 16px;
	box-shadow: 0 10px 30px rgb(20 20 31 / 16%);
}

.chip .head {
	display: flex;
	gap: 8px;
	align-items: center;
	justify-content: space-between;
}

.chip .title {
	flex: none;
	font-size: 15px;
	font-weight: 700;
	line-height: 1.3;
	overflow-wrap: anywhere;
	white-space: normal;
}

/* Two lines of the body, the rest opens in the widget. */
.chip .lead {
	display: -webkit-box;
	-webkit-box-orient: vertical;
	margin: 0;
	overflow: hidden;
	-webkit-line-clamp: 2;
	font-size: 13px;
	line-height: 1.45;
	color: var(--ink-subtle);
	overflow-wrap: break-word;
}

.chip .lead:empty {
	display: none;
}

.chip .picture {
	display: block;
	width: 100%;
	max-height: 150px;
	object-fit: cover;
	border-radius: 10px;
}

.chip .picture[data-hidden] {
	display: none;
}

.chip .actions {
	display: flex;
	gap: 8px;
	margin-top: 2px;
}

.chip .read,
.chip .got-it {
	display: inline-flex;
	align-items: center;
	height: 32px;
	padding: 0 14px;
	font: inherit;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	border-radius: 999px;
}

/* The main color when the config carries one (set on the host by
   `announcements.ts`); the chip's ink otherwise. */
.chip .read {
	color: #fff;
	background: var(--accent-read, var(--ink));
	border: 1px solid transparent;
}

.chip .got-it {
	color: var(--ink);
	background: #fff;
	border: 1px solid var(--hairline);
}

.chip .dismiss {
	margin-left: auto;
}

/* The chip's badge wears the kind's ink and wash, the same pairs as the
   widget panel, instead of the banner's white-on-brand treatment. */
.chip .badge {
	height: 19px;
	font-size: 10px;
	color: #5b3fd6;
	text-transform: none;
	letter-spacing: 0;
	background: #efeafd;
	border-radius: 999px;
}

.chip[data-kind="service_status"] .badge,
.chip[data-kind="fix"] .badge {
	color: #9a5b00;
	background: #fff2d8;
}

.chip[data-kind="business_update"] .badge,
.chip[data-kind="resource"] .badge {
	color: #1d5fb8;
	background: #e6f0fd;
}

.chip[data-kind="improvement"] .badge {
	color: #1e7f4f;
	background: #e3f6ec;
}

/* Only an announcement that asks for a confirmation carries "Got it". After
   both surfaces' button rules, so the hidden state wins the cascade. */
.banner .got-it[data-hidden],
.chip .got-it[data-hidden] {
	display: none;
}

/* --- narrow viewports --------------------------------------------------- */

@media (max-width: 480px) {
	.banner {
		gap: 8px;
		padding: 10px 8px 10px 12px;
	}

	.banner .badge {
		display: none;
	}

	.banner .view,
	.banner .got-it {
		padding: 0 12px;
	}
}

/* --- motion ------------------------------------------------------------- */

@keyframes wl-slide-down {
	from {
		transform: translateY(-100%);
	}

	to {
		transform: translateY(0);
	}
}

@keyframes wl-sheen {
	0% {
		background-position: -40% 0;
	}

	55%,
	100% {
		background-position: 140% 0;
	}
}

/* A single lean toward the pointer, so the button reads as pressable. */
@keyframes wl-nudge {
	0%,
	88%,
	100% {
		transform: translateX(0);
	}

	92% {
		transform: translateX(2px);
	}

	96% {
		transform: translateX(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.banner,
	.sheen,
	.banner .view {
		animation: none;
	}
}
